/**
 * The `open` message (docs/43 §6.1, slice 4a; crossmarc/steward-app#2699): "this login opened its
 * client record at this firm". The ONE message that makes a client's owner row, replacing the
 * draft `save` as the thing that creates it.
 *
 * Sent by the façade's authenticated routes (slice 4b, not built yet: nothing sends it in 4a) once
 * per (user, firm) session, and again as the REPAIR: a re-send is idempotent, so the façade's
 * repair trigger is just another `open`. `bind` cannot repair, because it carries no claim proof.
 *
 * ## What the message carries, and what the writer never takes from it
 *
 * `userId` and `email` come from appcore's answer to the caller's own token (the façade's rule for
 * every owner field), `invitationCode` is the code the login arrived with, and `consent` is the
 * façade's record of the cover agreement. The writer derives the client record's id, the draft id
 * and the binding proof from its own rows, never from this message.
 *
 * `consent` carries a time and a version, NOT the sentence: the writer composes the text itself
 * (`intakeSharingConsentSentence`), exactly as it does for a draft save, so a message cannot
 * choose what a client is recorded as having agreed to. `at` is the façade's clock at the moment
 * of the agreement, not the browser's. An absent `consent` is never written as an empty one.
 *
 * Strict, like every message on this queue.
 */
import { z } from 'zod';
import { ADVISOR_FIRM_SLUG_PATTERN } from './advisorFirmDirectory.js';
import { ADVISOR_CLIENT_EMAIL_MAX } from './advisorClient.js';
import { INTAKE_INVITATION_CODE_PATTERN } from './intakeDraft.js';

/** The `kind` the intake writer dispatches an open on, ahead of the draft family's check. */
export const CLIENT_RECORD_OPEN_KIND = 'open';

/** The most a consent version string may be: appcore's own bound on the stored value. */
export const CLIENT_SHARING_CONSENT_VERSION_MAX = 64;

export const ClientRecordOpenMessageSchema = z.strictObject({
  kind: z.literal(CLIENT_RECORD_OPEN_KIND),
  v: z.literal(1),
  userId: z.string().min(1).max(200).regex(/^[^\s#]+$/),
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  /** The signed-in account's verified address, from appcore. The proof for a public-page claim. */
  email: z.string().min(3).max(ADVISOR_CLIENT_EMAIL_MAX),
  /** The façade's clock when it accepted the open. Compared with a purge (`openedAt > purgedAt`) and with an address fence. */
  openedAt: z.iso.datetime().max(64),
  invitationCode: z.string().regex(INTAKE_INVITATION_CODE_PATTERN).optional(),
  consent: z.strictObject({
    // appcore's own bound (system-client-sharing-consent.ts, main 1f740b69, 2026-11-01): 40 characters, at most
    // 9 fractional digits. The writer also bounds it by now (clientOpen.ts `isAcceptableConsentAt`).
    at: z.iso.datetime().max(40).regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?Z$/),
    version: z.string().min(1).max(CLIENT_SHARING_CONSENT_VERSION_MAX),
  }).optional(),
});
export type ClientRecordOpenMessage = z.infer<typeof ClientRecordOpenMessageSchema>;

/** `undefined` for anything this deploy does not understand, the contract of `parseIntakeDraftMessage`. */
export function parseClientRecordOpenMessage(value: unknown): ClientRecordOpenMessage | undefined {
  const parsed = ClientRecordOpenMessageSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}
